import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/courses", label: "Courses" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/testimonials", label: "Testimonials" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function PublicLayout({ children }) {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState({});
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/site-settings").then(({ data }) => setSettings(data || {})).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen text-ink-900">
      {settings?.announcement_active && settings?.announcement_text && (
        <a href={settings.announcement_link || "/courses"} className="block bg-gold text-black text-center text-xs md:text-sm font-medium py-2.5 tracking-wide" data-testid="announcement-bar">
          {settings.announcement_text} <ChevronRight className="inline w-3 h-3 -mt-0.5" />
        </a>
      )}
      <header className="sticky top-0 z-50 glass-strong border-b border-ink-900/10">
        <div className="container-x flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-logo">
            <img
              src="https://customer-assets-0z36b82j.emergentagent.net/job_ai-automation-hub-411/artifacts/83lo68in_3559C803-2A81-4281-A39C-2F5CEA8693B0.png"
              alt="Niraj Bhaardwaj Academy logo"
              className="h-9 w-9 object-contain drop-shadow-[0_0_8px_rgba(184,137,75,0.35)]"
            />
            <span className="font-display font-bold text-lg tracking-tight">
              NIRAJ <span className="text-gold">BHAARDWAJ</span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} className={({ isActive }) => `text-sm ${isActive ? "text-ink-900" : "text-ink-600 hover:text-ink-900"} transition-colors`} data-testid={`nav-${n.label.toLowerCase()}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                {user.role === "admin" ? (
                  <Link to="/admin" className="text-sm text-ink-700 hover:text-ink-900" data-testid="header-admin-link">Admin</Link>
                ) : (
                  <Link to="/dashboard" className="text-sm text-ink-700 hover:text-ink-900" data-testid="header-dashboard-link">Dashboard</Link>
                )}
                <button className="btn-ghost !py-2 !px-4 text-sm" onClick={async () => { await logout(); navigate("/"); }} data-testid="header-logout">Logout</button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-sm text-ink-700 hover:text-ink-900" data-testid="header-login">Login</Link>
                <Link to="/courses" className="btn-neon !py-2 !px-5 text-sm" data-testid="header-cta">Explore courses</Link>
              </>
            )}
          </div>
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-ink-900/10 py-4 space-y-3 container-x">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className="block text-ink-800" data-testid={`mobile-nav-${n.label.toLowerCase()}`}>{n.label}</NavLink>
            ))}
            {user ? (
              <button onClick={async () => { await logout(); setOpen(false); navigate("/"); }} className="block text-ink-800" data-testid="mobile-logout">Logout</button>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="block text-ink-800" data-testid="mobile-login">Login</Link>
            )}
          </div>
        )}
      </header>
      <main>{children}</main>
      <Footer settings={settings} />
    </div>
  );
}

function Footer({ settings }) {
  return (
    <footer className="mt-32 border-t border-ink-900/10 py-16">
      <div className="container-x grid md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-2.5">
            <img
              src="https://customer-assets-0z36b82j.emergentagent.net/job_ai-automation-hub-411/artifacts/83lo68in_3559C803-2A81-4281-A39C-2F5CEA8693B0.png"
              alt="Niraj Bhaardwaj Academy logo"
              className="h-10 w-10 object-contain drop-shadow-[0_0_10px_rgba(184,137,75,0.4)]"
            />
            <div className="font-display font-bold text-xl">NIRAJ <span className="text-gold">BHAARDWAJ</span></div>
          </div>
          <p className="mt-3 text-ink-500 text-sm leading-relaxed">AI Expert • Automation Strategist • Business Builder</p>
        </div>
        <div>
          <div className="overline mb-3">Navigate</div>
          <ul className="space-y-2 text-sm text-ink-600">
            <li><Link to="/" className="hover:text-ink-900">Home</Link></li>
            <li><Link to="/courses" className="hover:text-ink-900">Courses</Link></li>
            <li><Link to="/about" className="hover:text-ink-900">About</Link></li>
            <li><Link to="/contact" className="hover:text-ink-900">Contact</Link></li>
            <li><Link to="/faq" className="hover:text-ink-900">FAQ</Link></li>
          </ul>
        </div>
        <div>
          <div className="overline mb-3">Legal</div>
          <ul className="space-y-2 text-sm text-ink-600">
            <li><Link to="/legal/terms" className="hover:text-ink-900">Terms & Conditions</Link></li>
            <li><Link to="/legal/privacy" className="hover:text-ink-900">Privacy Policy</Link></li>
            <li><Link to="/legal/refund" className="hover:text-ink-900">Refund Policy</Link></li>
          </ul>
        </div>
        <div>
          <div className="overline mb-3">Contact</div>
          <p className="text-sm text-ink-600" data-testid="footer-email">{settings?.contact_email}</p>
          <p className="text-sm text-ink-600 mt-1" data-testid="footer-phone">{settings?.contact_phone}</p>
          {settings?.address && (
            <p className="text-sm text-ink-600 mt-2 whitespace-pre-line" data-testid="footer-address">{settings.address}</p>
          )}
        </div>
      </div>
      <div className="container-x mt-12 pt-8 border-t border-ink-900/10 text-ink-500 text-xs flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="text-center md:text-left">
          <div>© 2026 Niraj Bhaardwaj Academy. All Rights Reserved.</div>
          <div className="mt-1" data-testid="footer-legal-entity">A subsidiary of <span className="text-ink-700">Bhaardwaj's Electronics's</span> · GST No. <span className="text-ink-700">03DOFPK8846Q1Z7</span></div>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden md:inline">Built for the AI-first generation.</span>
          <Link to="/admin/login" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-ink-900/15 text-ink-600 hover:text-gold hover:border-gold/40 transition-colors" data-testid="footer-admin-login">
            <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
            Admin login
          </Link>
        </div>
      </div>
    </footer>
  );
}
